/**
 * The one question pattern for every single- and multi-select question (docs/ui-text-style.md "Layout patterns"; Matt,
 * 2026-10-17): its own fieldset; the legend holds the question as a heading at the page's level (or is visually
 * hidden when a heading right above already says it); help text (smaller, muted) under it; the options, each with its
 * follow-up right under it; and, for a multi-select, "None of these" last and set apart. "None of these" is exclusive:
 * ticking it clears the others and ticking any other clears it, with the keyboard as with a mouse (native inputs).
 * Short radio sets (at most 3 options, each short, none with help or a follow-up) sit on one line on desktop and one
 * per line on phone (Matt, 2026-10-17; dead ends part 7).
 * Kept free of app state so the Form 106 chunk can be handed it (that chunk mustn't import src/ui).
 */

type El = <K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Record<string, string>, ...children: (Node | string)[]) => HTMLElementTagNameMap[K];

export interface ChoiceOption {
  value: string;
  label: string;
  /** A second line under the label (option cards). */
  help?: string;
  /** Shown right under the option, e.g. a date asked only when it's ticked. */
  followUp?: Node | string;
  disabled?: boolean;
  /** A small badge after the label, e.g. "Coming soon". */
  badge?: string;
}

export interface ChoiceSpec {
  /** The inputs' name; ids are `${idPrefix}${value}` (default prefix `${name}-`). */
  name: string;
  idPrefix?: string;
  type: 'radio' | 'checkbox';
  question: string;
  help?: string;
  /** The question's heading level: one below the heading of the screen or block it's in. */
  level: 2 | 3 | 4 | 5 | 6;
  /** The question is already the heading right above (e.g. a step's h2): the legend only names the group for screen readers. */
  hiddenLegend?: boolean;
  options: readonly ChoiceOption[];
  /** Multi-select only: the "None of these" label; exclusive, last, set apart. */
  none?: string;
  /** Multi-select only: "None of these" is the big first option, before the others (one tap settles the question for most people), not last. */
  noneFirst?: boolean;
  /** Radio: the chosen value ('' or absent = none). Checkbox: the ticked values; [] = "None of these"; undefined = not answered. */
  selected: string | readonly string[] | undefined;
  /** Radio: the value. Checkbox: the ticked values, [] for "None of these", undefined when everything is unticked. */
  onChange: (value: string | string[] | undefined) => void;
  /** Big option cards (the guided flow's steps) instead of a plain list. */
  cards?: boolean;
  /** Extra class on the fieldset (kept for existing styles and tests). */
  className?: string;
}

export function choiceQuestion(el: El, s: ChoiceSpec): HTMLFieldSetElement {
  const prefix = s.idPrefix ?? `${s.name}-`;
  const helpId = `${prefix}help`;
  const multi = s.type === 'checkbox';
  const ticked = multi ? (s.selected as readonly string[] | undefined) : undefined;
  const isChecked = (v: string) => (multi ? (ticked ?? []).includes(v) : s.selected === v);
  const choose = (value: string, checked: boolean) => {
    if (!multi) s.onChange(value);
    else if (value === NONE) s.onChange(checked ? [] : undefined);
    else {
      // Ticking an option clears "None of these" (it isn't in the list); unticking the last one = not answered.
      const next = [...(ticked ?? []).filter((x) => x !== value), ...(checked ? [value] : [])];
      s.onChange(next.length ? next : undefined);
    }
    // The question re-renders: keep the focus on the box just used.
    document.getElementById(prefix + value)?.focus();
  };
  const input = (value: string, checked: boolean, disabled?: boolean) => {
    const i = el('input', { type: s.type, name: s.name, value, id: prefix + value, ...(disabled ? { disabled: '' } : {}) });
    i.checked = checked;
    i.addEventListener('change', () => choose(value, i.checked));
    return i;
  };
  const option = (o: ChoiceOption) => {
    const box = input(o.value, isChecked(o.value), o.disabled);
    const label = s.cards
      ? el('label', { class: `option-card${o.disabled ? ' soon' : ''}`, for: prefix + o.value }, box,
        el('span', { class: 'option-text' }, el('span', { class: 'option-title' }, o.label), o.help ? el('span', { class: 'help' }, o.help) : '', o.badge ? el('span', { class: 'badge soon-badge' }, o.badge) : ''))
      : o.help
        ? el('label', { class: 'choice with-help' }, box, el('span', { class: 'option-text' }, o.label, el('span', { class: 'help' }, o.help)))
        : el('label', { class: 'choice' }, box, o.label, o.badge ? el('span', { class: 'badge soon-badge' }, o.badge) : '');
    return el('div', { class: 'question-option' }, label, o.followUp ? el('div', { class: 'follow-up' }, o.followUp) : '');
  };
  const heading = el(`h${s.level}` as 'h3', { class: 'question-title' }, s.question);
  const fieldset = el('fieldset', { class: `question${s.cards ? ' option-cards' : ''}${s.className ? ` ${s.className}` : ''}`, ...(s.help ? { 'aria-describedby': helpId } : {}) },
    s.hiddenLegend ? el('legend', { class: 'visually-hidden' }, s.question) : el('legend', {}, heading),
    s.help ? el('p', { id: helpId, class: 'help' }, s.help) : '',
    el('div', { class: `question-options${inline(s) ? ' inline' : ''}` }, ...s.options.map(option)));
  if (multi && s.none !== undefined) {
    const none = el('div', { class: `question-none${s.noneFirst ? ' first' : ''}` }, el('label', { class: 'choice' }, input(NONE, ticked !== undefined && ticked.length === 0), s.none));
    if (s.noneFirst) fieldset.querySelector(':scope > .question-options')?.before(none);
    else fieldset.append(none);
  }
  return fieldset;
}

export interface ChipSpec {
  /** Ids are `${idPrefix}${value}`. */
  idPrefix: string;
  /** The group's question, e.g. "In which years?". */
  question: string;
  chips: readonly { value: string; label: string }[];
  selected: readonly string[];
  onChange: (selected: string[]) => void;
  /** Shown under the chips while none is chosen: a missing answer, not an error (no alert, no red). */
  missing?: string;
}

/**
 * Chips (docs/ui-text-style.md "Layout patterns"): a set of short choices, each a real checkbox drawn as a button,
 * under the option that asks for them (as its follow-up). Ticked = filled with a check mark. None chosen with
 * `missing` set: the line says what is still needed.
 */
export function chipGroup(el: El, s: ChipSpec): HTMLFieldSetElement {
  const box = (c: ChipSpec['chips'][number]) => {
    const i = el('input', { type: 'checkbox', value: c.value, id: s.idPrefix + c.value, ...(s.missing && !s.selected.length ? { 'aria-describedby': `${s.idPrefix}missing` } : {}) });
    i.checked = s.selected.includes(c.value);
    i.addEventListener('change', () => {
      s.onChange(s.chips.map((x) => x.value).filter((v) => (v === c.value ? i.checked : s.selected.includes(v))));
      document.getElementById(s.idPrefix + c.value)?.focus();
    });
    return el('label', { class: 'chip' }, i, el('span', { class: 'chip-check', 'aria-hidden': 'true' }, '✓'), c.label);
  };
  const missingId = `${s.idPrefix}missing`;
  const group = el('fieldset', { class: 'chips', ...(s.missing && !s.selected.length ? { 'aria-describedby': missingId } : {}) },
    el('legend', { class: 'chips-title' }, s.question),
    el('div', { class: 'chips-row' }, ...s.chips.map(box)),
    s.missing && !s.selected.length ? el('p', { id: missingId, class: 'chips-missing', 'data-needed-line': '' }, s.missing) : '');
  return group;
}

const NONE = 'none';
/** Characters in an option label still "short" for one line on desktop (in the language shown). */
const SHORT = 24;
const inline = (s: ChoiceSpec) => s.type === 'radio' && !s.cards && s.options.length <= 3
  && s.options.every((o) => o.label.length <= SHORT && !o.help && !o.followUp && !o.badge);

/** A problem with what was typed, tied to its input when there is one. */
export interface FieldProblem {
  inputId?: string;
  text: string;
}

/**
 * The error pattern (docs/ui-text-style.md "Layout patterns", the manual form's): a summary first (role alert, focusable),
 * each item a link to its field; the field itself gets `aria-invalid` and the message right under it (`fieldError`).
 */
export function errorSummary(el: El, id: string, problems: readonly FieldProblem[]): HTMLElement | '' {
  if (!problems.length) return '';
  return el('div', { id, class: 'errors', role: 'alert', tabindex: '-1' }, el('ul', {}, ...problems.map((p) => {
    const target = p.inputId;
    if (!target) return el('li', {}, p.text);
    const go = el('a', { href: `#${target}` }, p.text);
    go.addEventListener('click', (e) => {
      e.preventDefault();
      document.getElementById(target)?.focus();
    });
    return el('li', {}, go);
  })));
}

/** The message under a field with a problem; marks the input invalid and describes it by the message. */
export function fieldError(el: El, input: HTMLElement, text: string | undefined): HTMLElement | '' {
  if (!text) return '';
  const id = `error-${input.id}`;
  input.setAttribute('aria-invalid', 'true');
  input.setAttribute('aria-describedby', [input.getAttribute('aria-describedby'), id].filter(Boolean).join(' '));
  return el('p', { id, class: 'field-error' }, text);
}
